import React from 'react';

import { Inject } from '@odinjs/odin';

import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent, HPMSOnCreateMenu, HPMSOnOptionClick, TokenUtils } from '@philips-emr/hpms-components';
import { FieldAPI, FileUploaderAPI, FormAPI, PhoneNumberAPI, Stamp, StampIcon, TabWithContentAPI } from '@philips-emr/tws-components';
import { Color, Country, ThemeTonalRange } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnBlur, OnChange, OnClick, OnDynamicContentRender, OnEdit, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, EditEvent, FetchEvent, MountEvent, RenderEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, ModalService } from '@philips-emr/tws-core/lib/services';

import { DadosPessoaisAliases, FragmentNames } from '../../codegen';
import { Endpoints } from '../../definitions/endpoints';
import { FileService, ToastService } from '../../services';
import BaseController from '../base/base-controller';

enum Status {
  APROVADO = 'A',
  REJEITADO = 'R',
  PENDENTE = 'P',
}

const OptionKey = {
  OBSERVACAO: 'observacao',
};

@Controller({ fragment: FragmentNames.DADOS_PESSOAIS })
class DadosPessoaisController extends BaseController {

  @Inject(DadosPessoaisAliases.FORM)
  private form: FormAPI;
  @Inject(DadosPessoaisAliases.FIELD_TERMO)
  private fieldTermo: FieldAPI;
  @Inject(DadosPessoaisAliases.FIELD_ANEXOS)
  private fieldAnexos: FieldAPI;
  @Inject(DadosPessoaisAliases.TAB_CHANGED_INFO)
  private TabInfoAlterada: TabWithContentAPI;

  @OnMount(DadosPessoaisAliases.FILE_UPLOADER)
  onFileuploaderMount = ({ target }: MountEvent): void => FileService.setPredefinedFiles(11, {
    field: this.fieldAnexos,
    fileUploader: target as FileUploaderAPI,
  });

  @OnMount(DadosPessoaisAliases.FORM)
  onDadosPessoaisMount(): void {
    this.form.setLocked(!TokenUtils.getAllowedFuncion().extBenefAltCadastral);
  }

  @OnMount(DadosPessoaisAliases.TAB_CHANGED_INFO)
  onTabMount(): void {
    this.TabInfoAlterada.setVisible(TokenUtils.getAllowedFuncion().extBenefAltCadastral);
  }

  @OnAfterFetch(DadosPessoaisAliases.FETCH_DADOS_PESSOAIS)
  onDadosPessoaisAfterFetch({ data }: FetchEvent): void {
    if (data) {
      const { sgEstado, nrTermoAlt } = data as { sgEstado: { vlDominio: string }, nrTermoAlt: number };
      this.fieldTermo.setVisible(!!nrTermoAlt);
      if (sgEstado && sgEstado.vlDominio) {
        this.context.set({ dsUnidadeFederacao: sgEstado.vlDominio });
        return;
      }
    }
    this.context.set({ dsUnidadeFederacao: '' });
  }

  @OnChange(DadosPessoaisAliases.FIELD_ESTADO)
  onChangeEstado({ value }: ChangeEvent<{ vlDominio: string }>): void {
    if (value) {
      const { vlDominio } = value;
      if (vlDominio) {
        this.context.set({ dsUnidadeFederacao: vlDominio });
        return;
      }
    }
    this.context.set({ dsUnidadeFederacao: '' });
  }

  @OnClick(DadosPessoaisAliases.LINK_TERMOS)
  onLinKTermoClick(): void {
    const { nrTermoAlt } = this.form.getRecord();
    if (nrTermoAlt) {
      void HttpService.get<{ termo: string }>(Endpoints.GET.TERMO_RESPONSABILIDADE, { nrSeqTermo: nrTermoAlt })
        .then(({ body }) => {
          if (body) {
            const { termo } = body;
            ModalService.openDefault({
              closeable: true,
              theme: ThemeTonalRange.ULTRA_LIGHT,
              title: 'beneficiary.alteracao-cadastral',
              children: (<div style={{ wordWrap: 'break-word' }} dangerouslySetInnerHTML={{ __html: termo }} />),
            });
          }
        })
        .catch(response => ToastService.open(response));
    }
  }

  @OnEdit(DadosPessoaisAliases.FORM)
  onEdit({ target }: EditEvent): void {
    const api = target as FormAPI;
    api.setRecord({ ...api.getRecord(), termo: false });
  }
  @OnSubmit(DadosPessoaisAliases.FORM)
  onSubmit({ target, record }: SubmitEvent): void {
    const { cdPessoaFisica } = this.context.getBulkData();
    const api = target as FormAPI;
    const body = this.getBody(api.getData(), record);
    api.setLoading(true);
    HttpService.post<{}>(Endpoints.POST.SOLIC_ALT_CADASTRO, { cdPessoaFisica }, { body })
      .then(response => {
        api.setLoading(false);
        ToastService.open(response);
        return false;
      })
      .catch(response => {        
        api.setLoading(false);
        api.setEditing(true);
        ToastService.open(response);
        return true;
      });
  }

  @OnBlur(DadosPessoaisAliases.FIELD_CEP)
  onCepBlur({ target }: FocusEvent): void {
    const api = target as unknown as FieldAPI;

    if (api.getValue()) {
      void HttpService.get<Record<string, any>>(Endpoints.GET.ENDERECO, { cdCep: api.getValue() })
        .then(({ body }) => {
          if (body) {
            const { dsBairro, dsComplemento, dsEndereco, estado, municipio } = body;
            this.form.setRecord({
              ...this.form.getRecord(),
              ...(dsBairro && { dsBairro }),
              ...(dsComplemento && { dsComplemento }),
              ...(dsEndereco && { dsEndereco }),
              ...(municipio && { cdMunicipioIbge: municipio }),
              ...(estado && { sgEstado: estado }),
            });
          }
        });
    }
  }

  @OnDynamicContentRender(DadosPessoaisAliases.CUSTOM_CARD_DADOS_PESSOAIS)
  onDynamicContentRender({ data, render }: RenderEvent): void {
    const { ieStatus } = data as { ieStatus: Status };
    const props = {
      [Status.APROVADO]: { icon: StampIcon.CHECK, color: Color.SAFETY_GREEN },
      [Status.PENDENTE]: { icon: StampIcon.CLOCK, color: Color.SAFETY_NEUTRAL },
      [Status.REJEITADO]: { icon: StampIcon.CANCELED, color: Color.SAFETY_RED },
    };
    render(() => <Stamp {...props[ieStatus]} label={`beneficiary.status-${ieStatus}`.toLowerCase()} />);
  }

  @HPMSOnCreateMenu(DadosPessoaisAliases.CUSTOM_CARD_DADOS_PESSOAIS)
  onCreateCardMenu({ data, shouldRender }: HpmsCustomCardMenuRenderEvent<Record<string, string>>): void {
    const { ieStatus } = data as { ieStatus: Status };
    if (ieStatus !== Status.REJEITADO) {
      shouldRender(false);
    }
  }

  @HPMSOnOptionClick(DadosPessoaisAliases.CUSTOM_CARD_DADOS_PESSOAIS)
  onOptionClick({ key, data }: HpmsCustomCardEvent<Record<string, string>>): void {
    const { nrSeqProtocolo } = data as unknown as { nrSeqProtocolo: number };

    const action = {
      [OptionKey.OBSERVACAO]: () => this.observacao(nrSeqProtocolo),
    };

    if (action[key]) {
      action[key]();
    }
  }

  @OnMount(DadosPessoaisAliases.PHONE_NUMBER)
  onMountPhone(event : MountEvent): void {
    const api = event.target as PhoneNumberAPI;
    api.setCountry(Country.BR);
  }

  @OnMount(DadosPessoaisAliases.CELLPHONE_NUMBER)
  onMountCellphone(event : MountEvent): void {
    const api = event.target as PhoneNumberAPI;
    api.setCountry(Country.BR);
  }

  private observacao = (nrSeqProtocolo: number): void => {
    const { nrSeqBeneficiario } = this.context.getBulkData();

    ModalService.openDefault({
      closeable: true,
      theme: ThemeTonalRange.ULTRA_LIGHT,
      fragment: FragmentNames.MODAL_OBSERVACOES_DADOS_PESSOAIS,
      contextData: { nrSeqBeneficiario, nrSeqProtocolo },
    });
  };

  private getBody = (data: Record<string, any>, record: Record<string, any>): {} => {
    const { nrSeqBeneficiario, cdPessoaFisica } = this.context.getBulkData();

    const campos: object[] = [];
    const nrSeqTermo = record.termo ? record.nrTermoAlt : '';
    const body = { nrSeqBeneficiario, cdPessoaFisica, nrSeqTermo, campos, dadosBeneficiario: record };

    if (!body.nrSeqTermo) {
      delete body.nrSeqTermo;
    }

    Object.keys(record).forEach(key => {
      if (!['termo', 'anexos'].includes(key)) {

        const dsValorOld = this.resolveMapper(data[key]);
        const dsValorNew = this.resolveMapper(record[key]);

        if (dsValorOld !== dsValorNew) {
          body.campos.push({ nmAtributo: key, dsValorOld, dsValorNew });
        }
      }
    });

    return body;
  };

  private resolveMapper(value: string | object | undefined): string {
    if (!value) {
      return '';
    }
    if (typeof value === 'object') {
      const { vlDominio, nrSequencia, cdMunicipioIbge } = value as Record<string, any>;
      return vlDominio || nrSequencia || cdMunicipioIbge || '';
    }
    return value;
  }
}

export {
  DadosPessoaisController,
};
